/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useCallback, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { SearchPageNode } from '@atlassian/search-client/search-page/types';

import { type ApplicationModes } from '../../constants/application-modes';
import { type PrimaryProductKey } from '../../constants/product-keys';
import type { ResultClickedCallback } from '../../constants/types';
import { useSearchCrossProductUrlWrapper } from '../../utils/use-cross-product-url-wrapper';
import { useAppContext } from '../app-context';
import { SearchMetadata } from '../search-metadata';
import { SearchResultTitle } from '../search-result-title';

import { messages } from './messages';

const searchResultContainerStyles = xcss({
	position: 'relative',
});

const iconAreaStyles = xcss({
	gridArea: 'icon',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
	width: token('space.600'),
});

const newIconStyles = xcss({
	marginBlockStart: 'space.075',
});

const bodyAreaStyles = xcss({
	gridArea: 'body',
	paddingTop: 'space.050',
	overflowWrap: 'break-word',
});

const bodyAreaHiddenStyles = xcss({
	display: 'none',
});

const footerAreaStyles = xcss({
	gridArea: 'footer',
});

const lockIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginInlineEnd: 'space.075',
	paddingTop: 'space.025',
});

const headerRightContainerStyles = xcss({
	gridArea: 'header-right',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'flex-end',
});

export type SearchResultProps = {
	id?: string;
	product?: string;
	productSelected?: boolean;
	primaryProduct?: PrimaryProductKey;
	primarySubProduct?: string;
	description?: ReactNode;
	footer?: ReactNode;
	icon: ReactNode;
	headerMetadataParts?: any[]; // TODO: CHANGE THIS TO A MORE SPECIFIC TYPE
	headerMetadataSocialParts?: any[]; // TODO: CHANGE THIS TO A MORE SPECIFIC TYPE
	titleMetadata?: ReactNode;
	title: ReactNode;
	url?: string;
	isThirdParty?: boolean;
	hideSummarizeInChat?: boolean;
	position?: number;
	/** The ARIA role for the search result */
	role?: string;
	/**
	 * @private
	 * @deprecated Passing in the full partial is ignored by the component. Pass in the required props directly instead.
	 * TODO: https://product-fabric.atlassian.net/browse/QS-7698
	 **/
	partial?: SearchPageNode;
	cloudId?: string;
	appMode?: ApplicationModes;
	/** Called when the result is clicked */
	onResultClicked?: ResultClickedCallback;

	/** Called when the result context menu is opened */
	onResultContextMenu?: VoidFunction;

	/** Called when the copy link button is clicked */
	onCopyLinkButtonClicked?: VoidFunction;

	/** Called when the summarize in chat button is clicked */
	onSummarizeInChatButtonClicked?: VoidFunction;

	/** Called when the context menu copy button is clicked */
	onContextMenuCopyButtonClicked?: VoidFunction;

	/** Called when the context menu button is clicked */
	onContextMenuButtonClicked?: VoidFunction;
	isLoadingSkeleton?: boolean;
	linkedResults?: SearchPageNode;
	isLinkedResult?: boolean;
	/** Used to render the lock icon in result context menu */
	isPrivate?: boolean;
	/** Used to determine if the result has any related links */
	hasRelatedLinks?: boolean;
	mainContentWidth?: number;
	/** Forwarded to SearchMetadata to derive the owning-site name at the display point. */
	selectedSites?: ReadonlyMap<string, string>;
};

/**
 * This component is intended to be as generic as possible.
 * It should provide generic "slots" for users of this component to add
 * their content in.
 */
export const SearchResult: ({
	id,
	product,
	productSelected,
	primaryProduct,
	primarySubProduct,
	description,
	footer,
	icon,
	url,
	hideSummarizeInChat,
	role,
	cloudId,
	title,
	isThirdParty,
	appMode,
	headerMetadataParts,
	headerMetadataSocialParts,
	titleMetadata,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	isLoadingSkeleton,
	isLinkedResult,
	isPrivate,
	hasRelatedLinks,
	mainContentWidth,
	selectedSites,
}: SearchResultProps) => JSX.Element = ({
	id,
	product,
	productSelected,
	primaryProduct,
	primarySubProduct,
	description,
	footer,
	icon,
	url,
	hideSummarizeInChat = false,
	role = 'listitem',
	cloudId, // required for AI Summaries
	title,
	isThirdParty,
	appMode,
	headerMetadataParts,
	headerMetadataSocialParts,
	titleMetadata,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	isLoadingSkeleton = false,
	isLinkedResult = false,
	isPrivate,
	hasRelatedLinks = false,
	mainContentWidth,
	selectedSites,
}: SearchResultProps) => {
	const { isRovoLLMEnabled } = useAppContext();
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	const isRovoLLMCheckPassed = !fg('kd_summarize_in_chat_rovo_llm_check') || isRovoLLMEnabled;

	const showSummarizeInChatByTitle = isRovoLLMCheckPassed && !hideSummarizeInChat;

	const [hasDescriptionContent, setHasDescriptionContent] = useState(
		isLoadingSkeleton ? true : !!description,
	);
	const descriptionRef = useCallback(
		(node: HTMLDivElement | null) => {
			// The loading skeleton intentionally renders a description made of empty
			// styled boxes (no text / media nodes). Skip the "is this empty?" heuristic
			// when rendering as a skeleton so the body row is preserved and the skeleton
			// keeps its full vertical height.
			if (isLoadingSkeleton) {
				return;
			}
			if (node) {
				const hasContent =
					(node.textContent?.trim().length ?? 0) > 0 ||
					node.querySelector('img, svg, canvas, video') !== null;
				if (!hasContent) {
					setHasDescriptionContent(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only re-check when description identity or skeleton mode changes
		[description, isLoadingSkeleton],
	);

	const wrapCrossProductUrl = useSearchCrossProductUrlWrapper({
		bridge: 'searchPage',
		primaryProduct,
		primarySubProduct,
	});
	const wrappedUrl = wrapCrossProductUrl(url, product);

	return (
		<Box
			role={role}
			onMouseEnter={() => {
				setIsHovered(true);
			}}
			onMouseLeave={() => setIsHovered(false)}
			testId={isLoadingSkeleton ? 'search-page-result-skeleton' : 'search-page-result'}
			ref={containerRef}
			xcss={searchResultContainerStyles}
		>
			<Grid
				columnGap="space.150"
				rowGap="space.050"
				templateAreas={[
					'icon heading header-right',
					'icon subheading-left header-right',
					...(hasDescriptionContent || isLoadingSkeleton ? ['icon body body'] : []),
					...(footer ? ['icon footer footer'] : []),
				]}
				templateColumns="auto 1fr auto"
			>
				<Box xcss={iconAreaStyles}>
					<Box xcss={newIconStyles}>{icon}</Box>
				</Box>
				<SearchResultTitle
					id={id}
					title={title}
					url={wrappedUrl}
					isThirdParty={isThirdParty}
					appMode={appMode}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					isHovered={isHovered}
					titleMetadata={titleMetadata}
					showSummarizeInChatButton={showSummarizeInChatByTitle}
					hasRelatedLinks={hasRelatedLinks}
				/>
				<SearchMetadata
					searchResultId={id}
					product={product}
					productSelected={productSelected}
					parts={headerMetadataParts}
					socialParts={headerMetadataSocialParts}
					containerRef={containerRef}
					selectedSites={selectedSites}
				/>
				<Box xcss={headerRightContainerStyles}>
					{isPrivate && (
						<Box xcss={lockIconStyles}>
							<Tooltip content={formatMessage(messages.messageResultLockIconTooltip)}>
								<LockLockedIcon
									label={formatMessage(messages.privateChannelLabel)}
									color={token('color.icon.accent.red')}
									size="small"
								/>
							</Tooltip>
						</Box>
					)}
				</Box>
				{description && (
					<Box
						ref={descriptionRef}
						xcss={[
							bodyAreaStyles,
							!hasDescriptionContent && !isLoadingSkeleton && bodyAreaHiddenStyles,
						]}
					>
						{description}
					</Box>
				)}
				{footer && <Box xcss={footerAreaStyles}>{footer}</Box>}
			</Grid>
		</Box>
	);
};
